"use client";

import { useEffect, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Capacitor } from "@capacitor/core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { signInAction, signUpWithEmail } from "@/app/actions/auth";
import { sendPasswordResetEmail } from "@/app/actions/password";

import { InAppBrowserBanner } from "@/components/auth/InAppBrowserBanner";
import { InAppBrowserGuide } from "@/components/auth/InAppBrowserGuide";
import { SocialLoginButton } from "@/components/auth/SocialLoginButton";
import {
  type OAuthProvider,
  getSocialProviders,
} from "@/components/auth/PROVIDER_ICONS";

import {
  startNativeSupabaseOAuth,
  startNativeNaverOAuth,
  startNativeLineOAuth,
} from "@/lib/auth/native-oauth";
import { checkInAppForOAuth } from "@/lib/auth/in-app-precheck";
import { getInAppBrowserType } from "@/lib/utils/in-app-browser";
import { redirectExternalWithFallback } from "@/lib/auth/external-redirect";
import { handleExternalLinkClick } from "@/lib/utils/external-link";
import { useInAppOAuthBlock } from "@/hooks/useInAppOAuthBlock";
import { getAuthErrorKey } from "@/lib/utils/error";
import { usePasswordValidation } from "@/hooks/usePasswordValidation";
import { hardNavigate } from "@/lib/navigation/hard-navigate";

export type AuthMode =
  | "select"
  | "email-login"
  | "email-signup"
  | "email-sent"
  | "forgot-password"
  | "forgot-password-sent";

export interface AuthFormSuccessPayload {
  /** Resolved redirect URL after successful email login. Modal callers may ignore. */
  redirectUrl: string;
  /**
   * True when redirectUrl is /verify-phone (phone not yet verified — e.g. an
   * email signup, which no longer collects a phone). Modal callers must
   * navigate instead of reloading.
   */
  needsPhone?: boolean;
}

export interface AuthFormProps {
  /** Layout context — controls a few minor render details. */
  context: "page" | "modal";
  /** Initial mode (page may receive `forgot-password` via ?mode=). */
  initialMode?: AuthMode;
  /** Return URL after login (page only; modal passes null/undefined). */
  returnUrl?: string | null;
  /** Called after successful email LOGIN (not signup — signup goes to email-sent screen). */
  onSuccess: (payload: AuthFormSuccessPayload) => void;
}

export function AuthForm({
  context,
  initialMode = "select",
  returnUrl,
  onSuccess,
}: AuthFormProps) {
  const t = useTranslations("common.auth");
  const tErrors = useTranslations("common.errors");
  const locale = useLocale();

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [lastLoginMethod, setLastLoginMethod] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState("");
  const [inAppGuideOpen, setInAppGuideOpen] = useState(false);
  // EULA consent (signup mode only; required by iOS App Store Guideline 1.2)
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const { isProviderBlocked } = useInAppOAuthBlock();

  // Read last login method from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("locarora_last_login_method");
      if (stored) setLastLoginMethod(stored);
    } catch {}
  }, []);

  const saveLastLoginMethod = (method: string) => {
    try {
      localStorage.setItem("locarora_last_login_method", method);
    } catch {}
  };

  const {
    password,
    passwordError,
    handlePasswordChange,
    validateAndSetError,
    markAsSubmitted,
    resetPasswordState,
  } = usePasswordValidation({ t, mode });

  const handleSocialLogin = async (provider: OAuthProvider) => {
    // 인앱 브라우저 사전 검사 — Google/Apple 등은 차단되므로 외부 브라우저로 우회.
    const precheck = checkInAppForOAuth(provider, window.location.href);
    if (precheck.action === "auto-redirect") {
      // UX: 갑작스러운 화면 전환 완화 — 짧은 안내 후 이동.
      // Naver 비공식 스킴은 무동작 가능 → 가이드 모달로 fallback.
      toast.info(t("redirectingToBrowser"));
      window.setTimeout(() => {
        redirectExternalWithFallback(precheck.url, () =>
          setInAppGuideOpen(true),
        );
      }, 250);
      return;
    }
    if (precheck.action === "show-guide") {
      setInAppGuideOpen(true);
      return;
    }

    setLoading(true);
    try {
      // Capacitor 네이티브 앱: Browser.open으로 외부 브라우저에서 OAuth 처리
      // (Google/Apple은 WebView 내 OAuth 차단, 세션은 딥링크로 복귀 후 setSession)
      if (
        Capacitor.isNativePlatform() &&
        Capacitor.isPluginAvailable("Browser")
      ) {
        saveLastLoginMethod(provider);
        if (provider === "naver") {
          await startNativeNaverOAuth(returnUrl || undefined, locale);
        } else if (provider === "line") {
          await startNativeLineOAuth(returnUrl || undefined, locale);
        } else {
          await startNativeSupabaseOAuth(
            provider,
            returnUrl || undefined,
            locale,
          );
        }
        return;
      }

      // Web flow: use route handlers (GET → JSON) instead of server actions.
      // Server-action responses in Next.js 16 + Node 22 occasionally fail with
      // a "transformAlgorithm is not a function" stream error, which surfaces
      // as the global error screen for every social-login attempt. Route
      // handlers skip the RSC encoding path and are unaffected.
      const buildStartUrl = (): string => {
        const params = new URLSearchParams();
        if (returnUrl) params.set("returnUrl", returnUrl);
        params.set("locale", locale);
        if (provider === "naver" || provider === "line") {
          return `/api/auth/${provider}/start?${params.toString()}`;
        }
        params.set("provider", provider);
        return `/api/auth/social-start?${params.toString()}`;
      };

      const response = await fetch(buildStartUrl(), {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const result = (await response.json().catch(() => null)) as {
        url?: string;
        error?: string;
      } | null;

      if (!response.ok || !result?.url) {
        const errorMessage = result?.error ?? "oauth_start_failed";
        const errorKey = getAuthErrorKey(errorMessage);
        toast.error(t("loginFailed"), {
          description: errorKey ? tErrors(errorKey) : errorMessage,
        });
      } else {
        saveLastLoginMethod(provider);
        hardNavigate(result.url);
      }
    } catch (error) {
      // Surface the actual error message so support / TestFlight users can
      // report what went wrong. USER_CANCELED from ASWebAuthenticationSession
      // is benign (user dismissed the sheet) — skip the toast in that case.
      const message =
        error instanceof Error ? error.message : String(error ?? "");
      if (message === "USER_CANCELED") {
        return;
      }
      console.error("[social-login] failed", error);
      toast.error(t("loginFailed"), {
        description: message || tErrors("auth.socialLoginError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    try {
      if (mode === "select" || mode === "email-login") {
        const result = await signInAction(formData);

        // Platform-blocked users: direct redirect to /blocked even from modal context.
        // Withdrawn-presentation blocks show the "account closed" framing via ?p=withdrawn.
        if ("blocked" in result && result.blocked) {
          const isWithdrawn =
            "presentation" in result && result.presentation === "withdrawn";
          hardNavigate(
            isWithdrawn
              ? `/${locale}/blocked?p=withdrawn`
              : `/${locale}/blocked`,
          );
          return;
        }

        if (result.error) {
          const errorKey = getAuthErrorKey(result.error);
          toast.error(t("loginFailed"), {
            description: errorKey ? tErrors(errorKey) : result.error,
          });
        } else {
          saveLastLoginMethod("email");
          toast.success(t("loginSuccess"), {
            description: t("welcomeBack"),
          });
          const targetLocale = result.preferredLanguage || locale;
          const destination = returnUrl
            ? decodeURIComponent(returnUrl)
            : `/${targetLocale}`;
          // Phone not verified yet (email signups never collect one) →
          // /verify-phone first (back = skip), then continue to destination
          // (the modal returns to the page it was opened on).
          const needsPhone = "needsPhone" in result && result.needsPhone === true;
          const phoneReturnTo =
            context === "modal"
              ? `${window.location.pathname}${window.location.search}`
              : destination;
          const redirectUrl = needsPhone
            ? `/${targetLocale}/verify-phone?returnTo=${encodeURIComponent(phoneReturnTo)}`
            : destination;
          onSuccess({ redirectUrl, needsPhone });
        }
      } else if (mode === "email-signup") {
        markAsSubmitted();

        const error = validateAndSetError();
        if (error) {
          setLoading(false);
          return;
        }

        if (!agreeTerms || !agreePrivacy) {
          toast.error(t("signupFailed"), {
            description: t("terms.consentRequired"),
          });
          setLoading(false);
          return;
        }

        // Phone is verified after signup (/verify-phone, enforced at
        // checkout) — same as social signups.
        const result = await signUpWithEmail({
          email: formData.get("email") as string,
          password: formData.get("password") as string,
          fullName: formData.get("fullName") as string,
          locale,
          acceptTerms: true,
          // Modal signup: after confirming the email (+ /verify-phone or
          // skip) come back to the page the modal was opened on.
          returnUrl:
            returnUrl ??
            (context === "modal"
              ? `${window.location.pathname}${window.location.search}`
              : null),
        });

        if (result.error) {
          const errorKey = getAuthErrorKey(result.error);
          toast.error(t("signupFailed"), {
            description: errorKey ? tErrors(errorKey) : result.error,
          });
        } else {
          setMode("email-sent");
        }
      }
    } catch {
      toast.error(t("loginFailed"), {
        description: tErrors("auth.loginError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await sendPasswordResetEmail(resetEmail, locale);

      if (result.error) {
        const errorKey = getAuthErrorKey(result.error);
        toast.error(t("resetPasswordFailed"), {
          description: errorKey ? tErrors(errorKey) : result.error,
        });
      } else {
        setMode("forgot-password-sent");
      }
    } catch {
      toast.error(t("resetPasswordFailed"), {
        description: tErrors("auth.loginError"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {mode === "select" && (
        <div className="space-y-8">
          {/* Welcome headline (page-only — modal already has its own title chrome) */}
          {context === "page" && (
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 md:text-2xl">
                {t("welcome")}
              </h2>
            </div>
          )}

          <InAppBrowserBanner />

          {/* Email login form — visible on the landing screen so users can
              sign in directly without an extra mode hop. */}
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <input type="hidden" name="locale" value={locale} />
            <Input
              name="email"
              type="email"
              placeholder={t("email")}
              required
              disabled={loading}
              className="h-12 rounded-full px-5 md:text-base"
            />
            <Input
              name="password"
              type="password"
              placeholder={t("password")}
              required
              minLength={8}
              disabled={loading}
              className="h-12 rounded-full px-5 md:text-base"
            />
            {/* "Last used: Email" hint sits right above the submit button so
                the badge has clear meaning ("you signed in with email last
                time") instead of just floating next to one input. Drawer's
                overflow can't clip it at this position either. */}
            {lastLoginMethod === "email" && (
              <div className="flex items-center justify-end gap-1.5 text-xs text-rose-500">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                <span>{t("recentlyUsedEmail")}</span>
              </div>
            )}
            <Button
              type="submit"
              className="h-12 w-full rounded-full bg-orange-500 text-base font-semibold text-white hover:bg-orange-600"
              disabled={loading}
            >
              {loading ? t("processing") : t("continue")}
            </Button>

            {/* Utility links — pivot to signup or password reset. */}
            <div className="flex items-center justify-center gap-4 pt-1 text-xs text-gray-500">
              <button
                type="button"
                onClick={() => {
                  setMode("email-signup");
                  resetPasswordState();
                }}
                className="underline-offset-2 hover:text-orange-500 hover:underline"
                disabled={loading}
              >
                {t("signup")}
              </button>
              <span aria-hidden className="text-gray-300">
                |
              </span>
              <button
                type="button"
                onClick={() => setMode("forgot-password")}
                className="underline-offset-2 hover:text-orange-500 hover:underline"
                disabled={loading}
              >
                {t("forgotPassword")}
              </button>
            </div>
          </form>

          {/* OR separator */}
          {/* <div className="relative">
            <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200" />
            <span className="relative mx-auto block w-fit bg-white px-4 text-xs text-gray-400">
              {t("or")}
            </span>
          </div> */}

          {/* Social Login Buttons — locale-specific provider order */}
          <div className="space-y-3">
            {getSocialProviders(locale).map((provider) => {
              const blocked = isProviderBlocked(provider);
              return (
                <SocialLoginButton
                  key={provider}
                  provider={provider}
                  onClick={() => handleSocialLogin(provider)}
                  disabled={loading}
                  isLastUsed={lastLoginMethod === provider}
                  blocked={blocked}
                />
              );
            })}
          </div>
        </div>
      )}

      {(mode === "email-login" || mode === "email-signup") && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {mode === "email-login" ? t("emailLogin") : t("emailSignup")}
          </h2>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <input type="hidden" name="locale" value={locale} />
            {mode === "email-signup" && (
              <div>
                <Input
                  name="fullName"
                  type="text"
                  placeholder={t("name")}
                  required
                  disabled={loading}
                  className="h-12 rounded-full px-5"
                />
              </div>
            )}

            <div>
              <Input
                name="email"
                type="email"
                placeholder={t("email")}
                required
                disabled={loading}
                className="h-12 rounded-full px-5"
              />
            </div>

            <div>
              <Input
                name="password"
                type="password"
                placeholder={t("password")}
                required
                minLength={8}
                disabled={loading}
                className="h-12 rounded-full px-5"
                value={password}
                onChange={handlePasswordChange}
                aria-invalid={mode === "email-signup" && !!passwordError}
              />
              {mode === "email-signup" &&
                (passwordError ? (
                  <p className="text-xs text-red-500 mt-1">{passwordError}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {t("passwordMinLength")}
                  </p>
                ))}
            </div>

            {mode === "email-signup" && (
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="signup-agree-terms"
                    checked={agreeTerms}
                    onCheckedChange={(v) => setAgreeTerms(v === true)}
                    disabled={loading}
                    className="h-5 w-5 border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  />
                  <Label
                    htmlFor="signup-agree-terms"
                    className="flex-1 text-gray-700 font-normal cursor-pointer leading-snug"
                  >
                    {t("terms.agreeTerms")}{" "}
                    <a
                      href={`/${locale}/help/terms`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 underline-offset-2 hover:underline"
                      onClick={handleExternalLinkClick(`/${locale}/help/terms`)}
                    >
                      {t("terms.view")}
                    </a>
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="signup-agree-privacy"
                    checked={agreePrivacy}
                    onCheckedChange={(v) => setAgreePrivacy(v === true)}
                    disabled={loading}
                    className="h-5 w-5 border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  />
                  <Label
                    htmlFor="signup-agree-privacy"
                    className="flex-1 text-gray-700 font-normal cursor-pointer leading-snug"
                  >
                    {t("terms.agreePrivacy")}{" "}
                    <a
                      href={`/${locale}/help/privacy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 underline-offset-2 hover:underline"
                      onClick={handleExternalLinkClick(
                        `/${locale}/help/privacy`,
                      )}
                    >
                      {t("terms.view")}
                    </a>
                  </Label>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-full"
              disabled={
                loading ||
                (mode === "email-signup" &&
                  (!agreeTerms || !agreePrivacy))
              }
            >
              {loading
                ? t("processing")
                : mode === "email-login"
                ? t("login")
                : t("signup")}
            </Button>
          </form>

          <div className="text-center text-sm mt-4">
            {mode === "email-login" ? (
              <p className="text-gray-600">
                {t("noAccount")}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("email-signup");
                    resetPasswordState();
                  }}
                  className="text-orange-500 hover:underline font-medium"
                  disabled={loading}
                >
                  {t("signup")}
                </button>
              </p>
            ) : (
              <p className="text-gray-600">
                {t("hasAccount")}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("email-login");
                    resetPasswordState();
                  }}
                  className="text-orange-500 hover:underline font-medium"
                  disabled={loading}
                >
                  {t("login")}
                </button>
              </p>
            )}
          </div>
        </>
      )}

      {mode === "email-sent" && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-orange-500" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("signupSuccess")}
          </h2>

          <p className="text-gray-600 mb-6">{t("signupSuccessMessage")}</p>

          <Button
            onClick={() => setMode("email-login")}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {t("goToLogin")}
          </Button>
        </div>
      )}

      {mode === "forgot-password" && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("forgotPasswordTitle")}
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            {t("forgotPasswordDescription")}
          </p>

          <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
            <div>
              <Input
                name="email"
                type="email"
                placeholder={t("email")}
                required
                disabled={loading}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="h-12 rounded-full px-5 md:text-base"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-full"
              disabled={loading}
            >
              {loading ? t("processing") : t("sendResetLink")}
            </Button>
          </form>

          <div className="text-center text-sm mt-4">
            <button
              type="button"
              onClick={() => {
                setMode("select");
                setResetEmail("");
              }}
              className="text-gray-600 hover:text-orange-500 hover:underline"
              disabled={loading}
            >
              {t("backToLogin")}
            </button>
          </div>
        </>
      )}

      {mode === "forgot-password-sent" && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-orange-500" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("resetEmailSent")}
          </h2>

          <p className="text-gray-600 mb-6">{t("resetEmailSentDescription")}</p>

          <Button
            onClick={() => {
              setMode("select");
              setResetEmail("");
            }}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {t("backToLogin")}
          </Button>
        </div>
      )}

      <InAppBrowserGuide
        open={inAppGuideOpen}
        onOpenChange={setInAppGuideOpen}
        browserType={getInAppBrowserType()}
      />
    </>
  );
}
